import React, { useState, useEffect } from 'react';
import { XCircle } from 'lucide-react';
import StarRating from '../Anime/StarRating';
import { getEpisodeDetailedRatingAverage } from '../../utils/animeUtils';
import { useLanguage } from '../../hooks/useLanguage';

const EpisodeRatingModal = ({ 
  episode, 
  anime, 
  onSave, 
  onCancel, 
  onDropAnime, 
  loading 
}) => {
  const { t } = useLanguage();
  const [ratingData, setRatingData] = useState({
    rating: 0,
    detailedRating: { content: 0, animation: 0, sound: 0 },
    notes: '',
    storyTitle: ''
  });

  useEffect(() => {
    if (episode) {
      setRatingData({
        rating: episode.rating || 0,
        detailedRating: episode.detailedRating || { content: 0, animation: 0, sound: 0 },
        notes: episode.notes || '',
        storyTitle: episode.storyTitle || ''
      });
    }
  }, [episode]);

  const handleSave = () => {
    onSave(
      ratingData.rating,
      ratingData.notes,
      ratingData.detailedRating,
      ratingData.storyTitle
    );
  };

  if (!episode) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md">
        <h2 className="text-xl font-bold dark:text-gray-100 mb-6">
          {t('episodeRating', { number: episode.number })}
        </h2>

        <div className="space-y-6">
          {anime.ratingMode === 'simple' ? (
            <div className="text-center">
              <StarRating
                rating={ratingData.rating}
                size={36}
                interactive={true}
                allowHalf={true}
                onRatingChange={(rating) => {
                  setRatingData({ ...ratingData, rating });
                }}
              />
              <p className="mt-3 text-lg font-medium">
                {ratingData.rating > 0 ? `${ratingData.rating.toFixed(1)} / 5.0` : t('noRating')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('content')}</label>
                <StarRating
                  rating={ratingData.detailedRating?.content || 0}
                  size={28}
                  interactive={true}
                  allowHalf={true}
                  onRatingChange={(rating) => {
                    setRatingData({
                      ...ratingData,
                      detailedRating: {
                        ...ratingData.detailedRating,
                        content: rating
                      }
                    });
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('animation')}</label>
                <StarRating
                  rating={ratingData.detailedRating?.animation || 0}
                  size={28}
                  interactive={true}
                  allowHalf={true}
                  onRatingChange={(rating) => {
                    setRatingData({
                      ...ratingData,
                      detailedRating: {
                        ...ratingData.detailedRating,
                        animation: rating
                      }
                    });
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('sound')}</label>
                <StarRating
                  rating={ratingData.detailedRating?.sound || 0}
                  size={28}
                  interactive={true}
                  allowHalf={true}
                  onRatingChange={(rating) => {
                    setRatingData({
                      ...ratingData,
                      detailedRating: {
                        ...ratingData.detailedRating,
                        sound: rating
                      }
                    });
                  }}
                />
              </div>
              {(() => {
                const avg = getEpisodeDetailedRatingAverage({ detailedRating: ratingData.detailedRating });
                return avg > 0 && (
                  <div className="text-center text-lg font-medium text-gray-700 dark:text-gray-300">
                    {t('averageRating')}: {avg.toFixed(1)} / 5.0
                  </div>
                );
              })()}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('storyTitle')}</label>
            <input
              type="text"
              value={ratingData.storyTitle}
              onChange={(e) => setRatingData({ ...ratingData, storyTitle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={t('example') + ': Meeting with ○○'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('memo')}</label>
            <textarea
              value={ratingData.notes}
              onChange={(e) => setRatingData({ ...ratingData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder={t('memoPlaceholder')}
            />
          </div>

          {!anime.dropped && (
            <div className="flex items-center gap-2 text-red-600">
              <button
                onClick={() => onDropAnime(episode.number)}
                className="text-sm underline hover:no-underline"
              >
                {t('dropHere')}
              </button>
              <XCircle size={16} />
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {loading ? t('saving') : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EpisodeRatingModal;